import { AlarmClock, BarChart2, Calendar, ChevronDown, EllipsisVertical, MoreVertical } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useRef, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { updateTableSize } from "@/api/table-floor/tableFloor";
import API from "@/api/lib/api";
import { fetchAllTables, openAddNewBookingTableModal, fetchAndReplaceBooking, removeTableFromList } from "@/redux/slice/table-slice/table-slice";
import AddEditTable from "@/components/backoffice/table-floor/AddEditTable";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import { formatTime, getDuration, isCurrentBooking } from "@/lib/bookingUtils";
import AddNewBookingForm from "../bookTable/AddNewBookingForm";

export default function LiveTableSidebar({
  selectedTableSizeUpdate,
  isItemEdit,
}) {
  const { selectedFloor, selectedRoom, tables } = useSelector((state) => state.table.table);
  const dispatch = useDispatch();

  const [deleteDialog, setDeleteDialog] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isMiniModalOpen, setIsMiniModalOpen] = useState(false);
  const [bookingItemForEdit, setBookingItemForEdit] = useState(null);
  const [floorNameForBookingForm, setFloorNameForBookingForm] = useState(null)
  // console.log(tempSelectedIdForCanvasEdit);
  
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().slice(0, 10);
  };

  const [selectedDate, setSelectedDate] = useState(getTodayDate());

  const [viewBy, setViewBy] = useState("");
  const [openView, setOpenView] = useState(false);

  const dateRef = useRef(null);

  const formattedDate = new Date(selectedDate).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const handleMenuOpen = (e, index) => {
    const rect = e.currentTarget.getBoundingClientRect();

    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.left - 100,
    });
    setIsMiniModalOpen(true);
    setBookingItemForEdit(index);
  };

  const handleClose = () => {
    setIsMiniModalOpen(!isMiniModalOpen);
  };
  const [formType, setFormType] = useState("");

  const updateTable = async () => {
    try {
      let res;

      res = await updateTableSize(selectedTableSizeUpdate.id, { table_size: selectedTableSizeUpdate.table_size });

      if (res.status === "success") {
        toast.success("Table updated successfully!");
        // refresh the tables for current room from server (preferred)
        if (selectedRoom?.id) dispatch(fetchAllTables(selectedRoom.id));
        // fallback: if server doesn't return updated list, we could also dispatch updateTableInList
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process Floor Card");
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialog(false);
  };

  const deleteHandler = async () => {
    try {
      await API.delete(`restaurant/bookings/${bookingItemForEdit.id}`);
      toast.success("Deleted successfully");
      // if server deletion succeeded, refresh tables of current room
      if (selectedRoom?.id) {
        dispatch(fetchAllTables(selectedRoom.id));
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!.");
    } finally {
      closeDeleteDialog();
    }
  };

  const isSameDate = (reservationDate, selectedDate) => {
    if (!reservationDate || !selectedDate) return false;
      return reservationDate.slice(0, 10) === selectedDate;
  };

  const isTableStatusMatched = (tableStatus, viewBy) => {
    if (!viewBy) return true; // All
    return tableStatus === viewBy;
  };
  const hasAnyBooking = tables.filter((t) => t.is_visible !== false).some((t) => {
    if (!isTableStatusMatched(t.status, viewBy)) return false;

    // table free হলে booking না থাকলেও valid
    if (t.status === "free") return true;

    return (t.bookings || []).some((booking) =>
      isSameDate(booking.reservation_date, selectedDate)
    );
  });

  // const [hasFetched, setHasFetched] = useState(false);
  useEffect(() => {
    if (
      // !hasFetched &&
      // isAddNewBookingTableModalOpen &&
      // (formType === "update" || formType === "view") &&
      bookingItemForEdit &&
      !bookingItemForEdit?.customer?.number
    ) {
      dispatch(fetchAndReplaceBooking(bookingItemForEdit?.id));
      // setHasFetched(true);
    }
  }, [bookingItemForEdit]);

  return (
    <div className=" w-[300px] tab-max:w-96 h-[calc(100vh-120px)] py-5 bg-[#1b1a1a] border rounded shadow border-gray-700 flex flex-col mr-2.5 ">
      <div className="pl-4 pr-2">
        <p className="text-xl font-medium">Table Management</p>
        {/* Date Picker */}
        {/* DATE (text button, not input) */}
        <div className="relative mt-3">
          <button
            onClick={() => dateRef.current.showPicker()}
            className="flex items-center gap-2 text-sm text-gray-300 hover:text-white"
          >
            <Calendar size={16} />
            <span>{formattedDate}</span>
            <ChevronDown size={14} />
          </button>
          <input
            ref={dateRef}
            type="date"
            className="absolute opacity-0 top-0 pointer-events-none"

            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>
      {/* RESERVATION HEADER */}
      <div className="px-4 py-3 pb-2 flex items-center justify-between">
        <h3 className="font-semibold">Reservation</h3>

        {/* VIEW BY */}
        <div className="relative">
          <button
            onClick={() => setOpenView(!openView)}
            className="flex items-center gap-2 bg-[#262626] px-3 py-1.5 rounded-md text-sm"
          >
            <BarChart2 size={14} />
            <span className="text-gray-300">{viewBy ? viewBy : 'View by'}</span>
            <ChevronDown size={14} />
          </button>

          {openView && (
            <div className="absolute right-0 mt-2 w-44 bg-[#262626] rounded-md overflow-hidden shadow-lg">
              {[{ label: "All", value: "" },
                { label: "Occupied", value: "occupied" },
                { label: "Checked-in / Waiting", value: "checked_in" },
                { label: "Free", value: "free" },
                { label: "Reserved", value: "reserved" }
              ].map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => {
                    setViewBy(item.value);
                    setOpenView(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 border-gray-700 border-b ${
                    viewBy === item.value ? "bg-white/10" : ""
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto ml-4 mr-2">
        {!hasAnyBooking && (
          <p className="text-center text-gray-500 mt-10">
            No reservation
          </p>
        )}
        {tables.filter((t) => t.is_visible !== false).map((t) => {
          if (!isTableStatusMatched(t.status, viewBy)) return null;

          const bookings = (t.bookings || []).filter((booking) =>
            isSameDate(booking.reservation_date, selectedDate)
          );

          return (
            <div key={t.id} className="border-b border-white/10">
              {/* {bookings.length === 0 && (
                <div className="py-4 flex justify-between">
                  <div>
                    <div className="flex gap-3 items-center">
                      <p className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center text-xs font-bold">
                        {t.name}
                      </p>
                      <p className="text-sm font-normal">Free</p>
                    </div>

                    <p className="text-sm font-normal mt-3">No reservation</p>
                  </div>

                  <div
                    className="cursor-pointer"
                    onClick={(e) => handleMenuOpen(e, t)}
                  >
                    <EllipsisVertical />
                  </div>
                </div>
              )} */}

              {/* যদি এক বা একাধিক booking থাকে */}
              {bookings.map((booking, index) => {                
                const isActive = isCurrentBooking(
                  booking.reservation_date,
                  booking.start_time,
                  booking.end_time
                );
                return (
                  <div
                    key={booking.id}
                    className="py-4 flex justify-between border-t border-white/10"
                  >
                    {/* LEFT */}
                    <div>
                      <div className="flex gap-3 items-center">
                          <p className="w-7 h-7 rounded-full bg-red-500 flex items-center justify-center text-xs font-bold">
                            {t.name}
                          </p>

                        <p className="text-sm font-normal">
                          {booking.customer?.full_name || booking.customer_full_name || booking.customer_name || "Guest"}
                        </p>
                      </div>

                      <p className="text-sm font-normal mt-3">
                        x{booking.guest} |{" "}
                        {formatTime(booking.start_time)} -{" "}
                        {formatTime(booking.end_time)}
                      </p>
                    </div>

                    {/* RIGHT */}
                    <div className="flex items-center gap-3">
                      {isActive && 
                        <div className="flex items-center gap-1 text-yellow-400 text-sm">
                          <AlarmClock size={18} />
                          <p className="text-[18px] font-medium">{booking.end_time ? getDuration(booking.start_time, booking.end_time) : "Running"}</p>
                        </div>
                      }
                      <div
                        className="cursor-pointer"
                        onClick={(e) => {handleMenuOpen(e, booking); setFloorNameForBookingForm(t.name)}}
                      >
                        <EllipsisVertical />
                      </div>
                    </div>
                  </div>
              )})}
            </div>
          );
        })}

        {isItemEdit && (
          <div className="text-end">
            <button className="text-xs font-normal px-4 py-1 text-white bg-[#34C759] m-3 rounded-sm" onClick={updateTable}>
              Save
            </button>
          </div>
        )}
      </div>

      {/* OVERLAY (for outside click) */}
      {isMiniModalOpen && <div className="fixed inset-0 z-[999]" onClick={handleClose}></div>}

      {/* MINI MODAL */}
      {isMiniModalOpen && (
        <div
          className="fixed z-[1000] bg-[#3a3a3a] border border-gray-600 rounded shadow-lg w-36"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
        >
          <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { setFormType("update"); setIsMiniModalOpen(false); dispatch(openAddNewBookingTableModal()) }}>
            Edit
          </button>
          <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { setFormType("view"); setIsMiniModalOpen(false); dispatch(openAddNewBookingTableModal()) }}>
            View Details
          </button>
          {/* <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { setDeleteDialog(true); }}>
            Delete
          </button> */}
        </div>
      )}
{/* {console.log(bookingItemForEdit)} */}
      <AddNewBookingForm
        // isOpen={isAddNewBookingTableModalOpen}
        // onClose={closeAddNewBookingTableModal}
        formType={formType}
        setFormType={setFormType}
        bookingItemForEditId={bookingItemForEdit?.id}
        floorName={floorNameForBookingForm}
      />

      <DeleteConfirmDialog
        isOpen={deleteDialog}
        onClose={closeDeleteDialog}
        onConfirm={() => {
          deleteHandler();
        }}
      />
    </div>
  );
}
